import { useCallback, useContext, useEffect, useMemo } from 'react'

import {
  Box,
  Button,
  Chip,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import { useGridApiContext } from '@mui/x-data-grid-premium'
import {
  IconArrowBigDown,
  IconArrowBigUp,
  IconDownload,
  IconPinFilled,
  IconX,
} from '@tabler/icons-react'
import type { DropResult } from 'react-beautiful-dnd'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'

import { useAuth } from 'src/auth'
import { logger } from 'src/lib/logger'
import { modernButtonContainer } from 'src/lib/style'

import Row from '../Row/Row'
import WorkspaceMemberChip from '../WorkspaceMemberChip/WorkspaceMemberChip'

import ColumnSelectorChip from './ColumnSelectorChip'
import { viewManagerContainerSx, displaySettingsSx } from './styles'
import ViewCreate from './ViewCreate'
import ViewFilters from './ViewFilters'
import ViewsContext from './ViewsContext'
import type { ViewsContextValue } from './ViewsContext'
import ViewTab from './ViewTab'

interface ViewManagerProps {
  showDisplaySettings: boolean
  setShowDisplaySettings: (_) => void
  lastUpdatedAt: number
  setUserInteraction: (_: number) => void
}

const ViewManager: React.FC<ViewManagerProps> = ({
  showDisplaySettings,
  setShowDisplaySettings,
  lastUpdatedAt,
  setUserInteraction,
}) => {
  const viewsContext = useContext(ViewsContext) as ViewsContextValue
  const apiGridRef = useGridApiContext()
  const { currentUser } = useAuth()
  const {
    views,
    pinnedViews,
    currentView,
    loading,
    saving: _saving,
    activateView,
    movePin,
  } = viewsContext as ViewsContextValue

  const apiRef = useGridApiContext()

  const handleViewChange = useCallback(
    (viewId: string | null) => {
      if (!viewId) return

      const view = views.find((v) => v.id === viewId)
      if (view) {
        activateView(view)
      }
    },
    [views, activateView]
  )

  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      if (!result.destination || !pinnedViews) return

      const sourceIndex = result.source.index
      const destinationIndex = result.destination.index

      if (sourceIndex === destinationIndex) return

      try {
        logger.dev('moving pin', {
          arg1: pinnedViews[sourceIndex].id,
          destinationIndex,
        })
        await movePin(pinnedViews[sourceIndex].id, destinationIndex)
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err))
        logger.error(
          'Failed to update view positions during drag and drop',
          error,
          {
            viewIds: pinnedViews.map((v) => v.id),
            sourceIndex,
            destinationIndex,
          }
        )
      }
    },
    [pinnedViews, movePin]
  )

  const canRemoveViewPin = useMemo(() => {
    return pinnedViews?.length > 1
  }, [pinnedViews])

  const handleToggleSort = (field, remove = false) => {
    logger.dev('handleToggleSort', { field, remove })
    const currentSortModel = apiGridRef.current.getSortModel()

    if (remove) {
      apiGridRef.current.sortColumn(field, null)
    } else {
      const currentDirection = currentSortModel.find(
        (sort) => sort.field === field
      )?.sort

      apiGridRef.current.sortColumn(
        field,
        currentDirection === 'asc' ? 'desc' : 'asc'
      )
    }
  }

  const handleTogglePin = (side: 'left' | 'right') => {
    apiGridRef.current.setPinnedColumns({
      ...pinnedColumns,
      [side]: [],
    })
  }

  const sortsCount = apiGridRef.current.exportState().sorting.sortModel.length
  const filtersCount =
    apiGridRef.current.exportState().filter.filterModel.items.length
  const groupingsCount =
    apiGridRef.current.exportState().rowGrouping.model.length

  const pinnedColumns = apiGridRef.current.getPinnedColumns() || {
    left: [],
    right: [],
  }
  const pinnedColumnsCount =
    (pinnedColumns?.left?.length ?? 0) + (pinnedColumns?.right?.length ?? 0)

  const modifiersState = useMemo(() => {
    const total =
      sortsCount + filtersCount + groupingsCount + pinnedColumnsCount + 1
    return {
      total,
      sorts: sortsCount,
      filters: filtersCount,
      groupings: groupingsCount,
      pinnedColumns: pinnedColumnsCount,
    }
  }, [sortsCount, filtersCount, groupingsCount, pinnedColumnsCount])

  const handleFilterOpen = useCallback(() => {
    setShowDisplaySettings(true)
  }, [setShowDisplaySettings])

  useEffect(() => {
    const subs: VoidFunction[] = []

    subs.push(
      apiGridRef.current.subscribeEvent('preferencePanelOpen', handleFilterOpen)
    )

    return () => {
      subs.forEach((unsub) => {
        unsub()
      })
    }
  }, [apiGridRef, handleFilterOpen])

  return (
    pinnedViews && (
      <Box>
        <Row sx={viewManagerContainerSx}>
          <Row className="view-manager-tabs-container">
            <Row>
              {pinnedViews.length > 0 ? (
                <DragDropContext
                  onDragEnd={handleDragEnd}
                  onReady={(props) => logger.dev('onReady', props)}
                  onDragStart={(props) => logger.dev('onDragStart', props)}
                >
                  <Droppable
                    droppableId="pinned-views"
                    direction="horizontal"
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{ display: 'flex' }}
                      >
                        <Tabs
                          value={0}
                          variant="scrollable"
                          scrollButtons={false}
                          className="view-manager-tabs"
                          key={`view-manager-${currentView?.id || 'no-view'} ${lastUpdatedAt}`}
                        >
                          {pinnedViews.map((view, index) => (
                            <Draggable
                              key={view.id}
                              draggableId={view.id}
                              index={index}
                            >
                              {(providedDraggable, snapshot) => (
                                <div
                                  ref={providedDraggable.innerRef}
                                  {...providedDraggable.draggableProps}
                                  style={{
                                    ...providedDraggable.draggableProps.style,
                                    opacity: snapshot.isDragging ? 0.8 : 1,
                                  }}
                                >
                                  <ViewTab
                                    view={view}
                                    currentView={currentView}
                                    setCurrentView={handleViewChange}
                                    dragHandleProps={
                                      providedDraggable.dragHandleProps
                                    }
                                    canRemoveViewPin={canRemoveViewPin}
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </Tabs>
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <Tabs
                  value={loading ? 'loading' : 'all'}
                  className="view-manager-tabs"
                >
                  <Tab
                    value={loading ? 'loading' : 'all'}
                    disabled={loading}
                    className="view-tab"
                    label={
                      <Row>
                        <Typography>
                          {loading ? 'Loading...' : 'All'}
                        </Typography>
                      </Row>
                    }
                  />
                </Tabs>
              )}
              <Row className="view-create-row">
                <ViewCreate />
              </Row>
            </Row>

            <Row
              gap={1}
              className="view-tab-inner"
            >
              <Tooltip
                title="Export"
                arrow={true}
              >
                <IconButton
                  className="export-button"
                  onClick={() => {
                    apiGridRef.current.exportDataAsCsv()
                  }}
                >
                  <IconDownload
                    size={12}
                    stroke={2.5}
                  />
                </IconButton>
              </Tooltip>

              <Box
                sx={{ ...modernButtonContainer }}
                key={`display-settings-${lastUpdatedAt}`}
              >
                {!showDisplaySettings ? (
                  <Button
                    onClick={() => setShowDisplaySettings((prev) => !prev)}
                    size="small"
                    variant="outlined"
                    className="display-settings-button right flat"
                    startIcon={
                      <Row className="modifier-badge-row">
                        {modifiersState.sorts > 0 && (
                          <Box className="modifier-badge sorts" />
                        )}
                        {modifiersState.groupings > 0 && (
                          <Box className="modifier-badge groupings" />
                        )}
                        {modifiersState.filters > 0 && (
                          <Box className="modifier-badge filters" />
                        )}
                        {modifiersState.pinnedColumns > 0 && (
                          <Box className="modifier-badge pinned" />
                        )}
                        <Box className="modifier-badge columns" />
                        <Box className="modifiers-count">
                          <Typography>{modifiersState.total}</Typography>
                        </Box>
                      </Row>
                    }
                  >
                    {'Settings'}
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      setShowDisplaySettings(false)
                    }}
                    variant="outlined"
                    size="small"
                    startIcon={<IconX size={14} />}
                    className="display-settings-button right flat"
                  >
                    {'Hide settings'}
                  </Button>
                )}
              </Box>
            </Row>
          </Row>
        </Row>
        <Row
          sx={{
            ...displaySettingsSx,
            ...(showDisplaySettings
              ? { display: 'flex' }
              : { display: 'none' }),
          }}
          onMouseDown={() => setUserInteraction(Date.now())}
        >
          <Row className="chip-row display-settings-chip-row">
            {currentView?.creatorId &&
              currentView?.creatorId !== currentUser?.id && (
                <WorkspaceMemberChip
                  userId={currentView?.creatorId}
                  variant="outlined"
                  workspaceId={currentView?.workspaceId}
                  label="(no edits)"
                />
              )}
            <ColumnSelectorChip />
            {pinnedColumns?.left?.length > 0 && (
              <Chip
                size="small"
                variant="outlined"
                className="pinned"
                label={`${pinnedColumns.left.length} pinned to left`}
                icon={
                  <IconPinFilled
                    size={14}
                    className="pinned-left"
                  />
                }
                onDelete={() => handleTogglePin('left')}
                deleteIcon={<IconX />}
              />
            )}
            {pinnedColumns?.right?.length > 0 && (
              <Chip
                size="small"
                variant="outlined"
                className="pinned"
                label={`${pinnedColumns.right.length} pinned to right`}
                icon={<IconPinFilled size={14} />}
                onDelete={() => handleTogglePin('right')}
                deleteIcon={<IconX />}
              />
            )}
            {(apiRef?.current.getSortModel() || []).map((sort) => (
              <Chip
                key={`sort-${sort.field}-${lastUpdatedAt}`}
                size="small"
                variant="outlined"
                className="sorts"
                onDelete={() => handleToggleSort(sort.field, true)}
                deleteIcon={
                  <IconX
                    size={12}
                    stroke={2.5}
                  />
                }
                onClick={() => handleToggleSort(sort.field)}
                label={
                  apiRef?.current
                    .getAllColumns()
                    .find((c) => c.field === sort.field)?.headerName ||
                  sort.field
                }
                icon={
                  sort.sort === 'asc' ? (
                    <IconArrowBigUp
                      size={6}
                      stroke={2.5}
                    />
                  ) : (
                    <IconArrowBigDown
                      size={6}
                      stroke={2.5}
                    />
                  )
                }
              />
            ))}

            <ViewFilters />
          </Row>
        </Row>
      </Box>
    )
  )
}

export default ViewManager
